// src/components/workflow/StatusProgress.tsx
import React from 'react';
import { Check, X } from 'lucide-react';
import { StatusLaporan } from '../../types';

interface Props {
    currentStatus: StatusLaporan;
    validasiL1Skipped?: boolean;
}

const STEPS: { key: StatusLaporan; label: string; shortLabel: string }[] = [
    { key: 'draf', label: 'Draf', shortLabel: 'Draf' },
    { key: 'diajukan', label: 'Diajukan', shortLabel: 'Diajukan' },
    { key: 'validasi_l1', label: 'Validasi Dekan / Ketua Lembaga', shortLabel: 'V. Dekan/Lembaga' },
    { key: 'validasi_l2', label: 'Validasi Warek', shortLabel: 'V. Warek' },
    { key: 'antrean_rapim', label: 'RAPIM', shortLabel: 'RAPIM' },
    { key: 'diputuskan', label: 'Keputusan', shortLabel: 'Keputusan' },
    { key: 'berlangsung', label: 'PPEPP', shortLabel: 'PPEPP' },
    { key: 'selesai', label: 'Selesai', shortLabel: 'Selesai' },
];

const STATUS_ORDER: Record<string, number> = {};
STEPS.forEach((s, i) => { STATUS_ORDER[s.key] = i; });

// ✅ FIX #6: Gunakan nama status Indonesia yang benar
STATUS_ORDER['ditolak_l1'] = 1;
STATUS_ORDER['ditolak_l2'] = 2;

// ✅ FIX #7: Tambahkan status yang belum terdaftar
STATUS_ORDER['diarsipkan'] = 8;
STATUS_ORDER['diselesaikan_lokal'] = 8;

const StatusProgress: React.FC<Props> = ({ currentStatus, validasiL1Skipped }) => {
    const effectiveStatus = validasiL1Skipped && currentStatus === 'validasi_l1'
        ? 'validasi_l2'
        : currentStatus;

    const currentIndex = STATUS_ORDER[effectiveStatus] ?? -1;

    // ✅ FIX #6: Gunakan prefix 'ditolak' bukan 'rejected'
    const isRejected = currentStatus.startsWith('ditolak');

    // ✅ FIX #7: Deteksi status final (selesai, arsip, resolved local)
    const isCompleted = ['selesai', 'diarsipkan', 'diselesaikan_lokal'].includes(currentStatus);

    return (
        <div className="w-full">
            <div className="flex items-center justify-between relative">
                {/* Background line */}
                <div
                    className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-0.5 bg-neutral-border"
                    style={{ top: '16px' }}
                />

                {STEPS.map((step, i) => {
                    const isL1Step = step.key === 'validasi_l1';
                    const isSkippedL1 = Boolean(validasiL1Skipped && isL1Step);

                    const isPast = i < currentIndex;
                    const isCurrent = i === currentIndex && !isRejected && !isCompleted;
                    const isFinalDone = isCompleted && i <= currentIndex;
                    const isRejectedAt = isRejected && i === currentIndex;

                    return (
                        <div
                            key={step.key}
                            className="relative flex flex-col items-center z-10"
                            style={{ width: `${100 / STEPS.length}%` }}
                        >
                            {/* Connector line */}
                            {i > 0 && (
                                <div
                                    className={`absolute top-4 -translate-y-1/2 h-0.5 right-1/2 ${isPast || isCurrent || isFinalDone
                                            ? 'bg-teal-main'
                                            : 'bg-neutral-border'
                                        }`}
                                    style={{ width: '100%', left: '-50%' }}
                                />
                            )}

                            {/* Circle */}
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all
                                    ${isPast || isFinalDone ? 'bg-teal-main border-teal-main text-white' : ''}
                                    ${isCurrent ? 'bg-white border-teal-main text-teal-main ring-4 ring-teal-main/20' : ''}
                                    ${isRejectedAt ? 'bg-danger border-danger text-white ring-4 ring-danger/20' : ''}
                                    ${!isPast && !isCurrent && !isRejectedAt && !isFinalDone
                                        ? 'bg-white border-neutral-border text-neutral-text-secondary'
                                        : ''}
                                `}
                            >
                                {isPast || isFinalDone ? (
                                    <Check className="w-4 h-4" />
                                ) : isRejectedAt ? (
                                    <X className="w-4 h-4" />
                                ) : (
                                    i + 1
                                )}
                            </div>

                            {/* Label */}
                            <span
                                className={`mt-2 text-xs font-medium text-center leading-tight
                                    ${isCurrent ? 'text-teal-main font-bold' : ''}
                                    ${isRejectedAt ? 'text-danger font-bold' : ''}
                                    ${isPast || isFinalDone ? 'text-teal-dark' : ''}
                                    ${!isPast && !isCurrent && !isRejectedAt && !isFinalDone
                                        ? 'text-neutral-text-secondary'
                                        : ''}
                                `}
                            >
                                <span className="hidden sm:inline">{step.label}</span>
                                <span className="sm:hidden">{step.shortLabel}</span>
                            </span>

                            {isSkippedL1 && (
                                <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-neutral-border text-neutral-text-secondary">
                                    Tidak Diperlukan
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Rejected indicator */}
            {isRejected && (
                <div className="mt-3 text-center">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-danger/10 text-danger">
                        ⚠️ {currentStatus === 'ditolak_l1'
                            ? 'Ditolak pada Validasi Level 1 — Silakan perbaiki dan ajukan ulang'
                            : 'Ditolak pada Validasi Level 2 — Silakan perbaiki dan ajukan ulang'}
                    </span>
                </div>
            )}

            {/* Resolved locally indicator */}
            {currentStatus === 'diselesaikan_lokal' && (
                <div className="mt-3 text-center">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-teal-main/10 text-teal-main">
                        ✅ Diselesaikan langsung tanpa RAPIM
                    </span>
                </div>
            )}
        </div>
    );
};

export default StatusProgress;